import {
  Component,
  EventEmitter,
  Output,
  Input,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  Inject,
  PLATFORM_ID, HostListener
} from '@angular/core';
import {NgbDate, NgbDropdown, NgbOffcanvas, NgbOffcanvasRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from "@ngneat/transloco";
import {BreakpointObserver} from "@angular/cdk/layout";
import {isPlatformBrowser} from "@angular/common";
import {AbstractComponent} from "../abstract.component";
type YM = { year: number; month: number };
@Component({
  selector: 'app-month-picker-mobile',
  templateUrl: './month-picker-mobile.component.html',
  styleUrls: ['./month-picker-mobile.component.scss']
})
export class MonthPickerMobileComponent extends AbstractComponent{
  @Input() title : string = ""
  @Input() disablePastMonths = false;
  @Input() disableFutureMonths = false;
  @Input() chosenFrom?: { from?: NgbDate };
  @Input() chosenTo?: { to?: NgbDate };
  @Output() monthsSelected = new EventEmitter<{ from?: NgbDate, to?: NgbDate }>();

  @ViewChild('offcanvasDateRef') offcanvasTemplate?: TemplateRef<any>;
  @ViewChild('desktopDropdown', { static: false }) desktopDropdown?: NgbDropdown;

  private offcanvasInstance?: NgbOffcanvasRef;
  isBrowser: boolean = false;
  isDesktop: boolean = false;

  today = new Date();
  currentMonth = this.today.getMonth();
  currentYearNow = this.today.getFullYear();

  currentYear = new Date().getFullYear();
  startMonth?: YM;
  endMonth?: YM;

  months = [
    this.translocoService.translate('months.picker.short.january'),
    this.translocoService.translate('months.picker.short.february'),
    this.translocoService.translate('months.picker.short.march'),
    this.translocoService.translate('months.picker.short.april'),
    this.translocoService.translate('months.picker.short.may'),
    this.translocoService.translate('months.picker.short.june'),
    this.translocoService.translate('months.picker.short.july'),
    this.translocoService.translate('months.picker.short.august'),
    this.translocoService.translate('months.picker.short.september'),
    this.translocoService.translate('months.picker.short.october'),
    this.translocoService.translate('months.picker.short.november'),
    this.translocoService.translate('months.picker.short.december')
  ];


  constructor(private translocoService: TranslocoService, private offcanvasService: NgbOffcanvas,
              private breakpointObserver: BreakpointObserver, @Inject(PLATFORM_ID) private platformId: Object,) {
    super()
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.subscriptions.push(this.breakpointObserver
          .observe([`(max-width: 991.98px)`])
          .subscribe(result => {
            this.isDesktop = !result.matches;
          }));
    }
  }

  ngOnChanges(ch: SimpleChanges): void {
    if ('chosenFrom' in ch || 'chosenTo' in ch) {
      const f = this.chosenFrom?.from;
      const t = this.chosenTo?.to;

      if (f && t) {
        this.startMonth = { year: f.year, month: f.month - 1 };
        this.endMonth = { year: t.year, month: t.month - 1 };
      } else if (f) {
        this.startMonth = { year: f.year, month: f.month - 1 };
        this.endMonth = undefined;
      } else if (t) {
        this.startMonth = { year: t.year, month: t.month - 1 };
        this.endMonth = undefined;
      } else {
        this.startMonth = undefined;
        this.endMonth = undefined;
        this.currentYear = new Date().getFullYear();
        return;
      }
      this.currentYear = this.startMonth ? this.startMonth.year : new Date().getFullYear();
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      this.offcanvasService.dismiss();
      this.offcanvasInstance?.close();
      this.desktopDropdown?.close();
    }
  }

  onClear() {
    this.startMonth = undefined;
    this.endMonth = undefined;
    this.emitRange();
    this.offcanvasService.dismiss();
    this.offcanvasInstance?.close();
  }


  onClose() {
    this.offcanvasService.dismiss();
    this.offcanvasInstance?.close();
    this.desktopDropdown?.close();
  }

  private isLessOrEqualYM(a: YM, b: YM) {
    return a.year < b.year || (a.year === b.year && a.month <= b.month);
  }

  selectMonth(monthIndex: number): void {
    const clicked: YM = { year: this.currentYear, month: monthIndex };

    if (!this.startMonth || this.endMonth) {
      this.startMonth = clicked;
      this.endMonth = undefined;
    } else if (!this.endMonth) {
      if (this.isLessOrEqualYM(clicked, this.startMonth)) {
        this.endMonth = this.startMonth;
        this.startMonth = clicked;
      } else {
        this.endMonth = clicked;
      }
    }
  }

  private emitRange(): void {
    const range = this.getSelectedRange();
    this.monthsSelected.emit(range ?? { from: undefined, to: undefined });
  }

  getSelectedRange(): { from?: NgbDate, to?: NgbDate } | undefined {
    if (!this.startMonth) return undefined;

    const now = new Date();
    const currentNgbDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());

    const start = this.startMonth;
    const end = this.endMonth ?? this.startMonth;

    let from: NgbDate;
    const startNgbMonth = start.month + 1;

    if (this.disablePastMonths && start.year === currentNgbDate.year && startNgbMonth === currentNgbDate.month) {
      from = currentNgbDate;
    } else {
      from = new NgbDate(start.year, startNgbMonth, 1);
    }

    let to: NgbDate;
    const endNgbMonth = end.month + 1;
    const lastDay = new Date(end.year, endNgbMonth, 0).getDate();

    if (this.disableFutureMonths && end.year === currentNgbDate.year && endNgbMonth === currentNgbDate.month) {
      to = currentNgbDate;
    } else {
      to = new NgbDate(end.year, endNgbMonth, lastDay);
    }

    if (from.after(to)) {
      return { from: to, to: from };
    }
    return { from, to };
  }


  isInRange(monthIndex: number): boolean {
    if (!this.startMonth) return false;
    const start = this.startMonth;
    const end = this.endMonth ?? this.startMonth;
    const cell: YM = { year: this.currentYear, month: monthIndex };

    const startCell = this.isLessOrEqualYM(start, cell);
    const cellEnd = this.isLessOrEqualYM(cell, end);
    return startCell && cellEnd;
  }

  isDisabled(monthIndex: number): boolean {
    if (!this.disablePastMonths && !this.disableFutureMonths) return false;

    if (this.disablePastMonths && this.currentYear < this.currentYearNow) return true;

    if (this.disableFutureMonths && this.currentYear > this.currentYearNow) return true;

    if (this.disablePastMonths && this.currentYear === this.currentYearNow && monthIndex < this.currentMonth) {
      return true;
    }
    if (this.disableFutureMonths && this.currentYear === this.currentYearNow && monthIndex > this.currentMonth) {
      return true;
    }

    return false;
  }


  prevYear(): void {
    this.currentYear--;
  }

  nextYear(): void {
    this.currentYear++;
  }


  onApply() {
    this.emitRange();
    this.onClose();
  }

  onOpenMobileFilter() {
    if (this.isBrowser) {
      if (!this.offcanvasTemplate) {
        return;
      }

      this.onFilterClose();

      this.startMonth = this.chosenFrom?.from ? { year: this.chosenFrom.from.year, month: this.chosenFrom.from.month - 1 } : undefined;
      this.endMonth = this.chosenTo?.to ? { year: this.chosenTo.to.year, month: this.chosenTo.to.month - 1 } : undefined;
      this.currentYear = this.startMonth?.year ?? this.today.getFullYear();

      this.offcanvasInstance = this.offcanvasService.open(this.offcanvasTemplate, {
        position: 'bottom',
        panelClass: 'mobile-filter-canvas',
      });
    }
  }

  onDesktopDropdownOpen(isOpen: boolean) {
    if (isOpen) {
      this.startMonth = this.chosenFrom?.from ? { year: this.chosenFrom.from.year, month: this.chosenFrom.from.month - 1 } : undefined;
      this.endMonth = this.chosenTo?.to ? { year: this.chosenTo.to.year, month: this.chosenTo.to.month - 1 } : undefined;
      this.currentYear = this.startMonth?.year ?? this.today.getFullYear();
    }
  }

  onFilterClose(): void {
    this.offcanvasService.dismiss();
    this.offcanvasInstance?.close();
  }
}